import { z } from 'zod'

import { schemas } from '~interface/zod-schema'

import { FormStepId, InputStepId, SectionStepId } from './idRegistry'

export type FormNode = z.infer<typeof schemas.FormNodeCreate>
export type FormNodeTypeEnum = z.infer<typeof schemas.Type6b9Enum>
export type FormNodeIdType = FormStepId | SectionStepId | InputStepId
export const FormNodeType = schemas.Type6b9Enum
export type FormDataNode = z.infer<typeof schemas.FormDataNode>
export type InputStyleEnum = z.infer<typeof schemas.StyleEnum>
export const InputStyle = schemas.StyleEnum
export type FormStyleElement = z.infer<typeof schemas.FormStyleElement>
export const FormStyleElementEnum = schemas.FormStyleElementTypeEnum
export type FormStyleElementType = z.infer<typeof schemas.FormStyleElementTypeEnum>

export type StepCreate = z.infer<typeof schemas.StepCreate>
export type ChargeSubStepCreate = z.infer<typeof schemas.ChargeSubStepCreate>
export type CopySectionSubStepViewRequestData = z.infer<
  typeof schemas.CopySectionSubStepViewRequestData
>
export type CreateFormStyleElementViewRequestData = z.infer<
  typeof schemas.CreateFormStyleElementViewRequestData
>
export type CreateInputStepViewRequestData = z.infer<typeof schemas.CreateInputStepViewRequestData>
export type CreateSectionStepViewRequestData = z.infer<
  typeof schemas.CreateSectionStepViewRequestData
>
export type DeleteInputStepViewRequestData = z.infer<
  typeof schemas.DeleteInputSubstepViewRequestData
>
export type DeleteSectionSubstepViewRequestData = z.infer<
  typeof schemas.DeleteSectionSubstepViewRequestData
>
export type DeleteFormStyleElementViewRequestData = z.infer<
  typeof schemas.DeleteFormStyleElementViewRequestData
>
export type EditBillStepViewRequestData = z.infer<typeof schemas.EditBillStepViewRequestData>
export type EditDateTimeStepViewRequestData = z.infer<
  typeof schemas.EditDateTimeStepViewRequestData
>
export type EditFormStepViewRequestData = z.infer<typeof schemas.EditFormStepViewRequestData>
export type EditIdentityStepViewRequestData = z.infer<
  typeof schemas.EditIdentityStepViewRequestData
>
export type EditFormStyleElementRequestData = z.infer<
  typeof schemas.EditFormStyleElementViewRequestData
>
export type EditInputStepViewRequestData = z.infer<typeof schemas.EditInputStepViewRequestData>
export type EditSectionSubStepViewRequestData = z.infer<
  typeof schemas.EditSectionSubStepViewRequestData
>
export type EditStepViewRequestData = z.infer<typeof schemas.EditStepViewRequestData>
export type DeleteStepAssignmentViewRequestData = z.infer<
  typeof schemas.DeleteStepAssignmentViewRequestData
>

export type VisualGroup = {
  type: FormNodeTypeEnum
  nodes: Array<SectionStepId | InputStepId>
}

export enum FormEditingStatus {
  preSubmit = 'preSubmit',
  submitted = 'submitted',
  editing = 'editing',
}

export enum StepType {
  form = 'form',
  bill = 'bill',
}

export function getFormEditingStatus(isEditing: boolean, completed: boolean): FormEditingStatus {
  if (isEditing) {
    return FormEditingStatus.editing
  } else if (completed) {
    return FormEditingStatus.submitted
  } else {
    return FormEditingStatus.preSubmit
  }
}

export function getReadOnlyStatus(
  formEditingStatus: FormEditingStatus,
  applyView: boolean,
  inReview: boolean,
): boolean {
  if (
    formEditingStatus === FormEditingStatus.editing ||
    formEditingStatus === FormEditingStatus.preSubmit
  ) {
    return false
  } else if (formEditingStatus === FormEditingStatus.submitted && !applyView) {
    return true
  } else if (formEditingStatus === FormEditingStatus.submitted && applyView) {
    return !inReview
  }
  return true // shouldn't hit this
}

export function determineVisualGroups(
  sortedChildren: (SectionStepId | InputStepId)[],
  nodeInfoDict: Record<FormStepId | SectionStepId | InputStepId, FormNode>,
): VisualGroup[] {
  let currentGroup: Array<SectionStepId | InputStepId> = []
  const groups: VisualGroup[] = []
  sortedChildren.forEach((childId: SectionStepId | InputStepId, index) => {
    const childNode = nodeInfoDict[childId]
    if (!childNode) return
    if (childNode.type === FormNodeType.enum.input) {
      currentGroup.push(childId)
      // If this is the last child or next child is a section, finish the input group
      const isLastChild = index === sortedChildren.length - 1
      const nextChild = !isLastChild ? nodeInfoDict[sortedChildren[index + 1]] : null
      if (isLastChild || nextChild?.type === FormNodeType.enum.section) {
        groups.push({ type: FormNodeType.enum.input, nodes: [...currentGroup] })
        currentGroup = []
      }
    } else if (childNode.type === FormNodeType.enum.section && childNode.shouldRender) {
      // If we have pending inputs, add them as a group
      if (currentGroup.length > 0) {
        groups.push({ type: FormNodeType.enum.input, nodes: [...currentGroup] })
        currentGroup = []
      }
      groups.push({ type: FormNodeType.enum.section, nodes: [childId] })
    }
  })
  return groups
}
